import { memo } from "react";

import { Switch } from "antd";
import { type SwitchProps } from "antd/lib/switch";
import { useTracking } from "react-tracking";

import { cn } from "@btnet/utilities-core";

import "./BTSwitch.less";

export declare type BTSwitchSize = "small" | "default" | "large";

export interface IBTSwitchProps extends Omit<SwitchProps, "onChange" | "size"> {
  id: string;
  "data-testid": string;

  label?: string | JSX.Element;

  /** Pass setFieldValue */
  onCheckedChange: (field: string, value: boolean) => void;

  /**
   * @default light
   */
  theme?: "light" | "dark";

  /**
   * @default default
   */
  size?: BTSwitchSize;

  /**
   * Additional class name for the switch element
   * @default ""
   */
  switchClassName?: string;
}

function BTSwitchInternal({
  id,
  label,
  onCheckedChange,
  disabled,
  size = "default",
  theme = "light",
  className,
  switchClassName,
  ...restProps
}: IBTSwitchProps) {
  const { trackEvent, Track } = useTracking<Record<string, unknown>>({
    element: "Switch",
    uniqueId: restProps["data-testid"],
  });

  const handleChange = (checked: boolean) => {
    onCheckedChange(id, checked);
    trackEvent({
      event: "ValueChange",
      extraInfo: {
        checked: checked,
      },
    });
  };

  const isLarge = size === "large";

  return (
    <Track>
      <div
        className={cn(
          "BTSwitch",
          {
            "BTSwitch-disabled": disabled,
            "BTSwitch-dark": theme === "dark",
          },
          `BTSwitch-${size}`,
          className,
        )}
      >
        <label>
          <Switch
            className={cn("BTSwitch-input", switchClassName)}
            onChange={handleChange}
            size={isLarge ? "default" : size}
            {...(disabled ? { disabled: true } : {})}
            {...restProps}
          />
          {label}
        </label>
      </div>
    </Track>
  );
}

export const BTSwitch = memo(BTSwitchInternal);
